import { css } from "@emotion/core"
import PropTypes from "prop-types"
import Icon from "components/planning_center_icon"
import { Menu, MenuButton, MenuList, MenuLink } from "@reach/menu-button"
import { colors } from "components/shared/admin_colors"
import pcoUrl from "@planningcenter/url"
import { formatDateShort } from "utils/dates"
import { pluralize } from "utils/helpers"
import useFlipperFlag from "hooks/use_flipper_flag"

const hasDuplicatesDropdown = css`
  background-color: var(--t-fill-color-status-warning-ghost);
  color: var(--t-text-color-default-primary);
  border-color: transparent;
  &:hover,
  &[data-reach-menu-button][aria-expanded="true"],
  &[aria-expanded="true"] {
    background-color: var(--t-fill-color-status-warning-ghost);
    color: var(--t-text-color-default-primary);
  }
`
const inactiveDropdown = css`
  background-color: rgba(0, 0, 0, 0.25);
  border-color: transparent;
  &:hover,
  &[data-reach-menu-button][aria-expanded="true"],
  &[aria-expanded="true"] {
    background-color: rgba(0, 0, 0, 0.35);
  }
`

const ProfileDropdown = ({
  isOrgAdmin,
  isActingStaff,
  profileIsInactive,
  profileHasDuplicates,
  profileAccountCenterId,
  profilePermissionsUrl,
  profileCanBeDeleted,
  personCanManageProfilesInPeople,
  profileDuplicatesResolutionUrl,
  peopleProfileUrl,
  profileSetInactiveDate,
  duplicateCount
}) => {
  const personUrl = `${pcoUrl(window.railsEnv)("people")}/people/AC${profileAccountCenterId}`
  const useCrossAppPermissionsModal = useFlipperFlag("ROLLOUT_global_permissions_cross_app_modal")

  const showSweetAlert = () => {
    return alertModal(
      {
        title: "Hold on!",
        text: `To merge or delete a person from the Planning Center database, an Organization Administrator must delete them from People.`,
        type: "warning",
        showCancelButton: true,
        cancelButtonText: "Cancel",
        confirmButtonText: "Open in People",
        customClass: "warning",
        closeOnConfirm: true
      },
      () => {
        Turbolinks.visit(`${pcoUrl(window.railsEnv)("people")}/people/AC${profileAccountCenterId}`)
      }
    )
  }

  const handlePermissionsEdit = ev => {
    ev.preventDefault()
    PermissionsEditorModal.afterClose = data => data.saved && window.location.reload()
    PermissionsEditorModal.load(`/permissions_editor/permissions/${profileAccountCenterId}`)
  }

  const renderPermissionsLink = () => {
    return useCrossAppPermissionsModal ? (
      <MenuLink onClick={handlePermissionsEdit}>Manage permissions</MenuLink>
    ) : (
      <MenuLink href={profilePermissionsUrl}>Permissions</MenuLink>
    )
  }

  return (
    <Menu>
      <MenuButton
        className="ml-1 app-btn action-dropdown-btn btn2"
        css={[
          profileHasDuplicates ? hasDuplicatesDropdown : profileIsInactive ? inactiveDropdown : null
        ]}
      >
        {profileHasDuplicates ? (
          <Icon
            symbol="general#exclamation-triangle"
            className="mb-1p mr-4p"
            css={{ color: "var(--warning-alert--icon-color)" }}
          />
        ) : profileIsInactive ? (
          <Icon symbol="general#inactive" className="mr-4p" />
        ) : null}
        Actions
        <span data-reach-menu-button-toggle-icon className="ml-1">
          <Icon symbol="general#down-caret" />
        </span>
      </MenuButton>
      <MenuList>
        {profileHasDuplicates && (
          <>
            <div className="p-2 d-f">
              <Icon
                symbol="general#exclamation-triangle"
                style={{
                  color: "var(--warning-alert--icon-color)",
                  flexShrink: 0,
                  fontSize: "1rem"
                }}
              />
              <div className="ml-1">
                There {pluralize("is", "are", duplicateCount, false)} {duplicateCount} potential{" "}
                {pluralize("duplicate", "duplicates", duplicateCount, false)} for this profile.{" "}
                {personCanManageProfilesInPeople ? (
                  <a href={profileDuplicatesResolutionUrl}>Review in People</a>
                ) : (
                  <>An administrator can resolve them in People.</>
                )}
              </div>
            </div>
            <div className="line mb-2p" />
          </>
        )}
        {profileIsInactive && (
          <>
            <div className="p-2 d-f">
              <Icon symbol="general#inactive" style={{ color: colors.neutral_020, fontSize: 24 }} />
              <div className="ml-1">
                Set inactive on {formatDateShort(profileSetInactiveDate)}.{" "}
                {personCanManageProfilesInPeople ? (
                  <a href={peopleProfileUrl}>Reactivate in People</a>
                ) : (
                  <>An administrator can reactivate them in People.</>
                )}
              </div>
            </div>
            <div className="line mb-2p" />
          </>
        )}
        <button className="dropdown-menu-item w-100%" data-person-modal-id={profileAccountCenterId}>
          Edit profile
        </button>
        {renderPermissionsLink()}
        {profileCanBeDeleted && (
          <>
            {isOrgAdmin ? (
              <button
                id="delete-person-js"
                className="dropdown-menu-item w-100%"
                data-person-url={personUrl}
                onClick={() => {
                  showSweetAlert()
                }}
              >
                Delete profile&hellip;
              </button>
            ) : (
              <div className="c-neutral-600 dropdown-menu-item" disabled>
                Delete profile&hellip;
                <div className="mt-1 p-1 action-drawer c-neutral-600 font-size-5">
                  To delete this profile, an administrator must remove them from People.
                </div>
              </div>
            )}
          </>
        )}
      </MenuList>
    </Menu>
  )
}

ProfileDropdown.propTypes = {
  isOrgAdmin: PropTypes.bool.isRequired,
  isActingStaff: PropTypes.bool.isRequired,
  profileIsInactive: PropTypes.bool.isRequired,
  profileHasDuplicates: PropTypes.bool.isRequired,
  profileAccountCenterId: PropTypes.number.isRequired,
  profilePermissionsUrl: PropTypes.string.isRequired,
  profileCanBeDeleted: PropTypes.bool.isRequired,
  personCanManageProfilesInPeople: PropTypes.bool.isRequired,
  profileDuplicatesResolutionUrl: PropTypes.string,
  peopleProfileUrl: PropTypes.string,
  profileSetInactiveDate: PropTypes.string,
  duplicateCount: PropTypes.number
}

export default ProfileDropdown
